import React, { createContext, useContext } from "react";
import classNames from "classnames";

/**
 * Context 생성
 * @param selectedValue 선택된 값
 * @param onChange 값 변경 핸들러
 * @param name 라디오 그룹 이름
 */
interface RadioContextType {
  selectedValue: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  name: string;
}
const RadioContext = createContext<RadioContextType | null>(null);

/**
 * RadioGroup 컴포넌트 (상태 관리자)
 * @param children 라디오 아이템
 * @param value 선택된 값
 * @param onChange 값 변경 핸들러
 * @param name 라디오 그룹 이름
 */
interface RadioGroupProps {
  children: React.ReactNode;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  name: string;
  direction?: "row" | "col";
}
export const RadioGroup = ({ children, value, onChange, name, direction = "col" }: RadioGroupProps): JSX.Element => (
  <RadioContext.Provider value={{ selectedValue: value, onChange, name }}>
    <div
      role="radiogroup"
      className={classNames("flex", direction === "row" ? "flex-row justify-between w-full" : "flex-col")}
    >
      {children}
    </div>
  </RadioContext.Provider>
);

/**
 *  Radio 컴포넌트 (개별 아이템)
 * @param id 라디오 아이템 고유 ID
 * @param name 라디오 그룹 이름
 * @param value 라디오 아이템 값
 * @param label 라디오 아이템 라벨
 * @param className 라디오 아이템 클래스
 */
interface RadioProps {
  id: string;
  name: string;
  value: string;
  label: string;
  className?: string;
  disabled?: boolean;
  size?: "sm" | "md";
}
export const Radio = ({
  id,
  name,
  value,
  label,
  className,
  disabled = false,
  size = "md",
}: RadioProps): JSX.Element => {
  const context = useContext(RadioContext);
  if (!context) {
    return <></>;
  }

  const { selectedValue, onChange } = context;
  const checked = selectedValue === value;

  return (
    <label
      htmlFor={id}
      className={classNames(
        "flex items-center justify-between border-b p-4 transition-colors last:border-b-0",
        {
          "cursor-pointer hover:bg-gray-50": !disabled,
          "cursor-not-allowed text-text-secondary": disabled,
        },
        className
      )}
    >
      <span className="text-black text-base font-normal">{label}</span>
      <div className="relative flex items-center">
        <input
          id={id}
          type="radio"
          name={name}
          value={value}
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className="absolute opacity-0"
        />
        <div
          className={classNames(
            "flex items-center justify-center rounded-full border border-solid transition-colors",
            size === "md" ? "w-5 h-5" : "w-4 h-4",
            {
              "border-solid border-gray-300 bg-gray-100 pointer-events-none": disabled,
              "border-brand-primary bg-white": !disabled && checked,
              "border-gray-500 bg-white": !disabled && !checked,
            }
          )}
        >
          {checked && (
            <div
              className={classNames(
                "rounded-full",
                size === "md" ? "w-4 h-4" : "w-3 h-3",
                disabled ? "bg-gray-400" : "bg-brand-primary"
              )}
            ></div>
          )}
        </div>
      </div>
    </label>
  );
};
